import { useCallback, useState, type ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';
import { useUploadCabinetFile } from '../../hooks/queries/useCabinetLibrary';
import { useAuthStore } from '../../store/useAuthStore';
import { CABINET_MAX_FILE_SIZE } from '../../services/cabinet/cabinetService';
import { formatFileSize } from '../../services/files/fileUtils';

interface Props {
  folderId: string;
  rootFolderId: string;
  disabled?: boolean;
  children: ReactNode;
}

interface PendingUpload {
  id: string;
  filename: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

export function CabinetDropzone({ folderId, rootFolderId, disabled, children }: Props) {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id ?? null);
  const upload = useUploadCabinetFile();
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const onDrop = useCallback(
    async (accepted: File[]) => {
      for (const file of accepted) {
        const id = crypto.randomUUID();
        if (file.size > CABINET_MAX_FILE_SIZE) {
          setPending((p) => [...p, {
            id, filename: file.name, status: 'error',
            error: `Trop volumineux (${formatFileSize(file.size)}, max 50 Mo)`,
          }]);
          continue;
        }
        setPending((p) => [...p, { id, filename: file.name, status: 'uploading' }]);
        try {
          await upload.mutateAsync({ file, folderId, rootFolderId, uploaderId: collaboratorId });
          setPending((p) => p.map((u) => u.id === id ? { ...u, status: 'done' } : u));
          setTimeout(() => setPending((p) => p.filter((u) => u.id !== id)), 2000);
        } catch (e) {
          setPending((p) => p.map((u) => u.id === id ? {
            ...u, status: 'error', error: e instanceof Error ? e.message : 'Erreur',
          } : u));
        }
      }
    },
    [collaboratorId, upload, folderId, rootFolderId],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop, disabled, noClick: true, noKeyboard: true,
  });

  return (
    <div {...getRootProps()} className="relative">
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-cime-primary/5 border-2 border-dashed border-cime-primary rounded-md">
          <p className="text-sm text-cime-primary font-medium">Déposez les fichiers ici</p>
        </div>
      )}

      {!disabled && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="text-xs text-neutral-500">
            Glissez-déposez des fichiers, ou
            <button
              type="button"
              onClick={open}
              className="ml-1 text-cime-primary hover:underline"
            >
              parcourez votre ordinateur
            </button>.
          </p>
          <button
            type="button"
            onClick={open}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-neutral-200 hover:bg-neutral-50"
          >
            <Upload className="w-4 h-4" /> Importer
          </button>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {pending.map((u) => (
            <div
              key={u.id}
              className={
                'flex items-center justify-between text-xs px-3 py-1.5 rounded-md border ' +
                (u.status === 'error' ? 'border-red-200 bg-red-50 text-red-700'
                 : u.status === 'done' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                 : 'border-neutral-200 bg-neutral-50 text-neutral-700')
              }
            >
              <span className="truncate">{u.filename}</span>
              <span>
                {u.status === 'uploading' && 'Envoi…'}
                {u.status === 'done' && '✓ Envoyé'}
                {u.status === 'error' && (u.error ?? 'Erreur')}
              </span>
            </div>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}
